import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistance } from 'date-fns';
import Page from '../../../components/Page';
import Breadcrumb from '../../../components/Breadcrumb';
import client from '../../../lib/feathers';
import scanAssetUrl from '../../../lib/scanAssetUrl';

const PAGE_SIZE = 50;

const STATUS_BADGE = {
  matched: 'bg-green-100 text-green-800',
  candidates: 'bg-amber-100 text-amber-800',
  unresolved: 'bg-red-100 text-red-800',
};

function StatusBadge({ value }) {
  if (!value) return <span className="text-gray-400">—</span>;
  const cls = STATUS_BADGE[value] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${cls}`}>
      {value}
    </span>
  );
}

function ScoreBar({ score }) {
  if (score === null || score === undefined) return <span className="text-gray-400">—</span>;
  const pct = Math.max(0, Math.min(1, score)) * 100;
  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <div className="flex-1 bg-gray-200 rounded-full h-1.5">
        <div className="bg-icon h-1.5 rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-600 tabular-nums">{score.toFixed(2)}</span>
    </div>
  );
}

// Build the API query from current filter state. Kept pure so the URL-sync
// useEffect can derive the canonical query without round-tripping through
// the network layer.
function buildQuery(filters, page) {
  const q = {
    $limit: PAGE_SIZE,
    $skip: page * PAGE_SIZE,
  };

  if (filters.status && filters.status !== 'all') q.matchResult = filters.status;
  if (filters.mapped && filters.mapped !== 'all') q.$mapped = filters.mapped;
  if (filters.userId) q.userId = filters.userId;
  if (filters.listingId) q.$listingId = filters.listingId;
  if (filters.scoreMin) q.$scoreMin = Number(filters.scoreMin);
  if (filters.scoreMax) q.$scoreMax = Number(filters.scoreMax);
  if (filters.extractedText) q.$extractedText = filters.extractedText;

  if (filters.daysBack) {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - Number(filters.daysBack));
    q.createdAt = { $gte: cutoff.toISOString() };
  }

  return q;
}

export default function BottleScans() {
  const [filters, setFilters] = useState({
    status: 'all',
    mapped: 'all',
    daysBack: '30',
    userId: '',
    listingId: '',
    scoreMin: '',
    scoreMax: '',
    extractedText: '',
  });
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await client.service('admin/libation-label-scan').find({
        query: buildQuery(filters, page),
      });
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load scans');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 0 whenever filters change.
  useEffect(() => { setPage(0); }, [filters]);

  const updateFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const totalPages = Math.ceil((data.total || 0) / PAGE_SIZE);

  return (
    <Page title="Bottle Scans">
      <div className="pb-5 border-b border-gray-200">
        <Breadcrumb elements={[{ name: 'Reports', href: '/admin/reports' }, { name: 'Bottle Scans' }]} />
        <h1 className="mt-2 text-2xl font-semibold text-gray-900">Bottle Scans</h1>
        <p className="mt-1 text-sm text-gray-500">
          Review what users scanned, what the AI extracted, and where the matcher routed them.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow-sm rounded-lg p-4 my-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <div>
          <label htmlFor="filter-status" className="block text-xs font-medium text-gray-600 mb-1">Match status</label>
          <select
            id="filter-status"
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
            className="block w-full rounded-md border-gray-300 text-sm py-1.5"
          >
            <option value="all">All</option>
            <option value="matched">Matched (auto-bond)</option>
            <option value="candidates">Candidates shown</option>
            <option value="unresolved">Unresolved</option>
          </select>
        </div>

        <div>
          <label htmlFor="filter-mapped" className="block text-xs font-medium text-gray-600 mb-1">User mapped</label>
          <select
            id="filter-mapped"
            value={filters.mapped}
            onChange={(e) => updateFilter('mapped', e.target.value)}
            className="block w-full rounded-md border-gray-300 text-sm py-1.5"
          >
            <option value="all">All</option>
            <option value="yes">Mapped to a wine</option>
            <option value="no">Not mapped (dropped)</option>
          </select>
        </div>

        <div>
          <label htmlFor="filter-days" className="block text-xs font-medium text-gray-600 mb-1">Last N days</label>
          <select
            id="filter-days"
            value={filters.daysBack}
            onChange={(e) => updateFilter('daysBack', e.target.value)}
            className="block w-full rounded-md border-gray-300 text-sm py-1.5"
          >
            <option value="">All time</option>
            <option value="7">7 days</option>
            <option value="30">30 days</option>
            <option value="90">90 days</option>
            <option value="365">365 days</option>
          </select>
        </div>

        <div>
          <label htmlFor="filter-extracted" className="block text-xs font-medium text-gray-600 mb-1">Search extracted text</label>
          <input
            id="filter-extracted"
            type="text"
            value={filters.extractedText}
            onChange={(e) => updateFilter('extractedText', e.target.value)}
            placeholder="name / producer"
            className="block w-full rounded-md border-gray-300 text-sm py-1.5"
          />
        </div>

        <div>
          <label htmlFor="filter-score-min" className="block text-xs font-medium text-gray-600 mb-1">Score min</label>
          <input
            id="filter-score-min"
            type="number"
            step="0.05"
            min="0"
            max="1"
            value={filters.scoreMin}
            onChange={(e) => updateFilter('scoreMin', e.target.value)}
            placeholder="0.0"
            className="block w-full rounded-md border-gray-300 text-sm py-1.5"
          />
        </div>

        <div>
          <label htmlFor="filter-score-max" className="block text-xs font-medium text-gray-600 mb-1">Score max</label>
          <input
            id="filter-score-max"
            type="number"
            step="0.05"
            min="0"
            max="1"
            value={filters.scoreMax}
            onChange={(e) => updateFilter('scoreMax', e.target.value)}
            placeholder="1.0"
            className="block w-full rounded-md border-gray-300 text-sm py-1.5"
          />
        </div>

        <div>
          <label htmlFor="filter-user" className="block text-xs font-medium text-gray-600 mb-1">User ID</label>
          <input
            id="filter-user"
            type="text"
            value={filters.userId}
            onChange={(e) => updateFilter('userId', e.target.value)}
            placeholder="UUID"
            className="block w-full rounded-md border-gray-300 text-sm py-1.5"
          />
        </div>

        <div>
          <label htmlFor="filter-listing" className="block text-xs font-medium text-gray-600 mb-1">Listing ID</label>
          <input
            id="filter-listing"
            type="text"
            value={filters.listingId}
            onChange={(e) => updateFilter('listingId', e.target.value)}
            placeholder="UUID"
            className="block w-full rounded-md border-gray-300 text-sm py-1.5"
          />
        </div>
      </div>

      {/* Results */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {loading ? 'Loading…' : `${data.total} scans`}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-2 text-sm">
              <button
                type="button"
                className="px-2 py-1 border rounded disabled:opacity-50"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                ←
              </button>
              <span className="text-gray-600">Page {page + 1} of {totalPages}</span>
              <button
                type="button"
                className="px-2 py-1 border rounded disabled:opacity-50"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                →
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="p-4 text-sm text-red-700 bg-red-50">{error}</div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Photo</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Extracted</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mapped to</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.data.map((scan) => {
                const ex = scan.extractedData || {};
                const lv = scan.libationVintage;
                const lib = lv?.libation;
                const listing = lib?.listing;
                const photoUrl = scanAssetUrl(scan);
                return (
                  <tr key={scan.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap">
                      {photoUrl ? (
                        <img
                          src={photoUrl}
                          alt=""
                          className="w-12 h-12 object-cover rounded"
                          onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded" />
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700" title={scan.createdAt}>
                      {formatDistance(new Date(scan.createdAt), new Date(), { addSuffix: true })}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">
                      {scan.user ? (
                        <Link to={`/admin/users/${scan.user.id}`} className="text-icon hover:underline">
                          {scan.user.firstName} {scan.user.lastName}
                        </Link>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs max-w-xs">
                      <div className="font-medium text-gray-900 truncate">{ex.name || <span className="text-gray-400">—</span>}</div>
                      <div className="text-gray-600 truncate">{ex.producer || ''}{ex.vintage ? ` · ${ex.vintage}` : ''}</div>
                      {ex.varietal && <div className="text-gray-500 truncate">{ex.varietal}</div>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap"><StatusBadge value={scan.matchResult} /></td>
                    <td className="px-3 py-2 whitespace-nowrap"><ScoreBar score={scan.matchScore} /></td>
                    <td className="px-3 py-2 text-xs">
                      {lib ? (
                        <div>
                          <Link
                            to={listing ? `/admin/listings/${listing.id}/wines/${lib.id}` : '#'}
                            className="text-icon hover:underline font-medium"
                          >
                            {lib.name}
                          </Link>
                          <div className="text-gray-500">
                            {listing?.name || lib.listingName || ''}
                            {lv?.vintage?.year ? ` · ${lv.vintage.year === 9999 ? 'NV' : lv.vintage.year}` : ''}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      <Link
                        to={`/admin/reports/bottle-scans/${scan.id}`}
                        className="text-icon hover:underline text-xs font-medium"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {!loading && data.data.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-3 py-8 text-center text-sm text-gray-500">
                    No scans match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Page>
  );
}
